import { useState } from "react";
import { useDispatch } from "react-redux";
import { evaluateFormula } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { SECTION_TYPE_TABLE_SECTION } from "@Helpers/constants";
import type { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";
import { t } from "@i18n";
import type { TranslatedString } from "@i18n";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_WARNING } from "@Services/constants";
import { attachmentFileUrl } from "@Utils";
import {
  buildConcatenatedValue,
  formSectionType,
  getPermissions,
  TEMPLATE_ENTITY_CREATION,
  TEMPLATE_ENTITY_SIDEBAR,
  toFiniteNumber,
} from "./constants";
import type { SectionPermissions, TableSectionRow } from "./constants";
import type {
  FieldAnswer,
  FieldFileValue,
  RemoveAllUploadedFilesArgs,
  RenderableQuestionField,
} from "./questionFieldsHelper";
import type { FormInstance } from "../../formInstance.wire";

/**
 * A section as these helpers read it. `Partial<FormTemplateField>` rather than the strict template
 * type because the same functions run against a live form instance, whose sections are looser
 * (`sectionHeading` is optional there), and against a half-built template in the form builder.
 * `permissions` exists only on instance sections, so it is spliced on separately.
 */
type ReadableSection = Partial<FormTemplateField> & { permissions?: SectionPermissions };

/** The form document as these helpers read it; only the captured template is ever touched. */
type ReadableFormData = { selectedForm?: { template?: Array<Partial<FormTemplateField>> } };

/**
 * `formTemplateFieldSchema.tableDataSource` is `z.array(z.unknown())`, so a table row arrives
 * untyped. The element really is the row shape modelled as `tableSectionRow` in
 * `forms/inspectionAndProgress.collection.ts`, which `forms.schema.ts` cannot import back without a
 * module cycle — hence a guard here rather than an assumption. It only asserts the part these
 * readers rely on: that the row is a keyed object (every cell read below goes through `?.value`,
 * which is `unknown` either way).
 */
const isTableSectionRow = (row: unknown): row is TableSectionRow =>
  typeof row === "object" && row !== null && !Array.isArray(row);

/**
 * One row of a section's `tableDataSource`. A missing row — an out-of-range `tableRowIndex`, or a
 * section that carries no rows — reads as an empty row, exactly as the `?? {}` it replaces did.
 * Non-object rows do not occur: the schema's cube-qa census records all 277 stored template
 * elements and all 1184 form-instance elements as plain objects.
 */
const readTableRow = (row: unknown): TableSectionRow => (isTableSectionRow(row) ? row : {});

/**
 * An answer `value` as the concatenate field joins it. `answerSchema.value` is `z.unknown()` on the
 * wire — the shape is chosen by the question's `fieldType`, so a number field really does store a
 * number — and `String` is what the join has always done to it.
 *
 * TRUTHINESS, not nullishness: a falsy answer (absent, `""`, `0`, `false`) maps to `""`, which
 * `buildConcatenatedValue` then drops with its `.filter(Boolean)` — the same entry the raw value
 * was dropped by before, so a `0` answer stays out of the joined text rather than becoming "0".
 */
const answerText = (value: unknown): string => (value ? String(value) : "");

/**
 * One entry of a `fileUpload` / `image` answer. Every member of `FieldFileValue` (the server's own
 * `formFileEntrySchema`) is optional, so "a plain object" is the whole contract; the guard only
 * keeps a malformed entry from being handed on as a file.
 */
const isFileValue = (entry: unknown): entry is FieldFileValue => typeof entry === "object" && entry !== null;

/**
 * Narrow a question to the stored shape the file-removal handler is written against.
 *
 * `eachField` is `Partial<QuestionField>` throughout this hook because the same code runs against a
 * half-built question in the template builder, while `RemoveAllUploadedFilesArgs` takes the full
 * question — so the three members `questionFieldSchema` makes required are checked here rather than
 * assumed. A question that is still being authored carries no uploaded files to clear.
 */
const isStoredQuestion = (field: Partial<QuestionField>): field is RenderableQuestionField =>
  field._id !== undefined && field.fieldType !== undefined && field.questionValue !== undefined;

/** Arguments shared by the formula and concatenate resolvers. */
type CalculatedFieldArgs = {
  eachField?: Partial<QuestionField>;
  section?: ReadableSection;
  tableRowIndex?: number;
  formData?: ReadableFormData;
};

/**
 * Hint shown under formula/concatenate fields while their value is still
 * incomplete (evaluates to null). Shared by the web and mobile renderers.
 */
export const getCalculatedFieldHintText = () =>
  t("This value is calculated automatically once all required fields are filled");

/**
 * Build the numeric scope for a formula field.
 *
 * A number field contributes its raw numeric answer. A referenced *formula*
 * field no longer resolves to `null` — instead its own expression is evaluated
 * recursively (against its own `fieldsVariableMapper`) so formulas can reference
 * other formula fields. The `visiting` set guards against cyclic references: a
 * formula that (transitively) references itself resolves to `null` rather than
 * recursing forever.
 *
 * @param rootId          `_id` of the formula field being rendered; seeds the
 *                        cycle guard so a referenced formula pointing back at it
 *                        resolves to `null`. Accepts `undefined` because callers
 *                        read it off a partially built question.
 * @param mapper          `fieldsVariableMapper` of the formula being rendered
 *                        (referenced field `_id` → variable name).
 * @param questionById    All candidate questions keyed by `_id`.
 * @param getRawValue     Raw (pre-`toFiniteNumber`) answer value for a field id.
 */
export const buildFormulaScope = (
  rootId: string | undefined,
  mapper: Record<string, string>,
  questionById: Map<string, QuestionField>,
  getRawValue: (id: string) => unknown,
): Record<string, number> => {
  const resolveValue = (question: QuestionField, visiting: Set<string | undefined>): number | null => {
    if (question.fieldType === "formula") {
      if (visiting.has(question._id)) return null;
      visiting.add(question._id);
      const nestedScope: Record<string, number> = {};
      Object.entries(question.fieldsVariableMapper ?? {}).forEach(([refId, varName]) => {
        const refQuestion = questionById.get(refId);
        if (!refQuestion || !varName) return;
        const value = resolveValue(refQuestion, visiting);
        if (value !== null) nestedScope[varName] = value;
      });
      visiting.delete(question._id);
      return evaluateFormula(question.formulaExpression || "", nestedScope);
    }
    return toFiniteNumber(getRawValue(question._id));
  };

  const scope: Record<string, number> = {};
  Object.entries(mapper).forEach(([refId, varName]) => {
    const question = questionById.get(refId);
    if (!question || !varName) return;
    const value = resolveValue(question, new Set([rootId]));
    if (value !== null) scope[varName] = value;
  });
  return scope;
};

export const collectNonTableQuestions = (template: Array<Partial<FormTemplateField>> = []): QuestionField[] =>
  template.flatMap((section) =>
    section.sectionCode === SECTION_TYPE_TABLE_SECTION ? [] : (section.questionFields ?? []),
  );

export const questionMapById = (questions: QuestionField[]): Map<string, QuestionField> =>
  new Map(questions.map((question) => [question._id, question]));

// Builds the numeric scope for a formula field (row-scoped for table sections,
// form-wide otherwise) and evaluates its expression. Referenced formula fields
// resolve recursively via buildFormulaScope. Returns null while any referenced
// field is still empty/non-numeric.
export const getFormulaFieldValue = ({
  eachField = {},
  section = {},
  tableRowIndex = 0,
  formData = {},
}: CalculatedFieldArgs) => {
  const fieldVariableMapper = eachField.fieldsVariableMapper ?? {};
  const formulaExpression = eachField.formulaExpression || "";

  let scope: Record<string, number>;
  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    const rowData = readTableRow(section.tableDataSource?.[tableRowIndex]);
    const questionById = questionMapById(section.questionFields ?? []);
    scope = buildFormulaScope(eachField._id, fieldVariableMapper, questionById, (id) => rowData[id]?.value);
  } else {
    const questionById = questionMapById(collectNonTableQuestions(formData.selectedForm?.template));
    scope = buildFormulaScope(
      eachField._id,
      fieldVariableMapper,
      questionById,
      (id) => questionById.get(id)?.answer?.value,
    );
  }

  return evaluateFormula(formulaExpression, scope);
};

// Resolves the values of the fields referenced by a concatenate field
// (row-scoped for table sections, form-wide otherwise) and joins them with the
// configured separator. Returns null while nothing resolves.
export const getConcatenateFieldValue = ({
  eachField = {},
  section = {},
  tableRowIndex = 0,
  formData = {},
}: CalculatedFieldArgs) => {
  const valueById = new Map<string, string>();

  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    const rowData = readTableRow(section.tableDataSource?.[tableRowIndex]);
    Object.keys(rowData).forEach((key: string) => {
      valueById.set(key, answerText(rowData[key]?.value));
    });
  } else {
    (formData.selectedForm?.template ?? []).forEach((templateSection) => {
      if (templateSection.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
      (templateSection.questionFields ?? []).forEach((question) => {
        valueById.set(question._id, answerText(question.answer?.value));
      });
    });
  }

  return buildConcatenatedValue(eachField.selectedFields, valueById, eachField.seperator) || null;
};

/**
 * How a platform puts downloaded files on the device: one file on its own, several under `archiveName`.
 *
 * The only part of a bulk download that cannot be shared. Web fetches each URL and hands the browser a
 * Blob (zipping when there is more than one) — `saveRemoteFiles` in `@Services/fileDownload`; native has
 * to go through the filesystem and the share sheet instead. Everything else — which entries can be
 * downloaded, what each is named, what the archive is named, the in-flight guard and the messaging — is
 * in `handleDownloadAllFiles` below and is identical on both.
 */
export type SaveRemoteFiles = (files: Array<{ name: string; url: string }>, archiveName: string) => Promise<void>;

/**
 * Makes `name` unique within `taken`, the way a file manager does: `shot.png`, `shot (1).png`, …
 *
 * Two uploads can carry the same name — nothing dedupes them on the way in — and an archive with
 * duplicate entries keeps only one of them.
 */
const uniqueFileName = (name: string, taken: Set<string>) => {
  if (!taken.has(name)) {
    taken.add(name);
    return name;
  }
  const dot = name.lastIndexOf(".");
  const [stem, extension] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ""];
  let suffix = 1;
  while (taken.has(`${stem} (${suffix})${extension}`)) suffix += 1;
  const candidate = `${stem} (${suffix})${extension}`;
  taken.add(candidate);
  return candidate;
};

/** `CUBE_Download_dd-mm-yy` — the name a multi-file download already carries elsewhere in the product. */
const bulkDownloadName = () =>
  `CUBE_Download_${new Date()
    .toLocaleDateString("en-IN", { year: "2-digit", month: "2-digit", day: "2-digit" })
    .replaceAll("/", "-")}`;

type UseQuestionFieldsArgs = {
  eachField?: Partial<QuestionField>;
  tableRowId?: string | number | null;
  extraParams?: Record<string, unknown>;
  section?: ReadableSection;
  sectionIndex?: number;
  fieldIndex?: number;
  tableRowIndex?: number;
  source?: string;
  formData?: Partial<FormInstance>;
  userId?: string;
  disabled?: boolean;
  showAnswersOnly?: boolean;
  templateSource?: string;
  handleCustomFieldData?: unknown;
  saveRemoteFiles?: SaveRemoteFiles;
  handleRemoveAllUploadedFiles?: (args: RemoveAllUploadedFilesArgs) => void;
};

function useQuestionFields({
  eachField = {},
  tableRowId = "",
  extraParams = {},
  section = {},
  sectionIndex,
  fieldIndex,
  tableRowIndex = 0,
  source = "",
  formData = {},
  userId = "",
  disabled = false,
  showAnswersOnly = false,
  templateSource = "FORM_FILLING",
  handleCustomFieldData = null,
  saveRemoteFiles,
  handleRemoveAllUploadedFiles,
}: UseQuestionFieldsArgs) {
  const dispatch = useDispatch();
  const questionId = eachField._id ?? "";

  // `uniqueId` is declared even though the seed below omits it: `questionFields` both writes it
  // (`setOpenUrlPopUpState({ state: true, url, uniqueId: name })`) and reads it back to decide which
  // field's popup is open. Inference from the seed alone would leave it off the type and reject both.
  // Type argument only — the seed value is unchanged, so nothing is emitted differently.
  const [openUrlPopUpState, setOpenUrlPopUpState] = useState<{ state: boolean; url: string; uniqueId?: string }>({
    state: false,
    url: "",
  });
  let name = tableRowId ? `${tableRowId}${questionId}` : questionId;
  if (extraParams.id !== undefined && extraParams.id !== null) {
    name = `${extraParams.id}_${name}`;
  }

  const sectionId = section._id ?? "";

  const { permissions, tableDataSource = [] } = section;

  // Annotated so the table-row branch — whose rows are `z.array(z.unknown())` on the wire, narrowed
  // by `readTableRow` — gives every consumer of this hook the same answer shape as the plain branch.
  let currentAnswer: FieldAnswer<unknown> = eachField.answer ?? {};
  if (source === formSectionType.TABLE_SECTION) {
    currentAnswer = readTableRow(tableDataSource[tableRowIndex])[questionId] ?? {};
  }
  const extraCheck = eachField.allowPredefinedVal || eachField.allowPreDefinedColValues;
  // The `extraCheck &&` the second half used to open with was dead: it is only reached when
  // `extraCheck` is truthy.
  const allowEditDuringFormFilling =
    !extraCheck || eachField.allowEditingOfPredefinedVal || section.allowEditPreDefineRowInForm;

  const isCurrentUserAllowedToFill = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Fill", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const isCurrentUserAllowedToModify = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Modify", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const enabled =
    (isCurrentUserAllowedToFill || isCurrentUserAllowedToModify) &&
    !showAnswersOnly &&
    ((["FORM_FILLING", TEMPLATE_ENTITY_CREATION, TEMPLATE_ENTITY_SIDEBAR].includes(templateSource) &&
      !!allowEditDuringFormFilling) ||
      (templateSource === "TEMPLATE_CREATION" && !!extraCheck));

  const formulaValue =
    eachField.fieldType === "formula" ? getFormulaFieldValue({ eachField, section, tableRowIndex, formData }) : null;

  const concatenatedValue =
    eachField.fieldType === "concatenate"
      ? getConcatenateFieldValue({ eachField, section, tableRowIndex, formData })
      : null;

  const autoNumberValue = currentAnswer.value ?? "";

  /* ---------------------------------------------------------------------------------------------- */
  /* Bulk actions on a `fileUpload` / `image` answer.                                                */
  /*                                                                                                 */
  /* At this scope rather than in `useForms` because both act on one field: the state is naturally    */
  /* per-field, where a form-wide hook would need a `uniqueId` to work out which field it belongs to. */
  /* Platform-neutral, so mobile gets them by passing the same two arguments web does.                */
  /* ---------------------------------------------------------------------------------------------- */

  const [isDownloadingAllFiles, setIsDownloadingAllFiles] = useState(false);
  const [isDeleteAllFilesModalOpen, setIsDeleteAllFilesModalOpen] = useState(false);

  /**
   * This answer's uploaded files, whichever kind of section holds it. The stored `value` is
   * `z.unknown()` on the wire, so the list is read through {@link isFileValue} rather than assumed
   * to be one — an answer of any other shape simply holds no files.
   */
  const answerFiles: FieldFileValue[] = Array.isArray(currentAnswer.value)
    ? currentAnswer.value.filter(isFileValue)
    : [];

  /**
   * Downloads every uploaded file of this answer.
   *
   * Works out which entries are downloadable, what each is called and what the archive is called, then
   * hands that list to the injected `saveRemoteFiles` — the only step that differs between a browser and
   * a device.
   *
   * Entries with no `awsKey` came from the File Manager and own no object of their own — they are
   * reachable only through the viewer — so they cannot be included, and the count is reported rather than
   * silently returning fewer files than the field shows.
   */
  const handleDownloadAllFiles = async () => {
    if (isDownloadingAllFiles) return;
    const downloadable = answerFiles.filter((file) => file.awsKey);
    const skippedCount = answerFiles.length - downloadable.length;

    if (downloadable.length) {
      setIsDownloadingAllFiles(true);
      const taken = new Set<string>();
      try {
        // A caller that surfaces bulk download has to inject the platform's save step. Failing loudly
        // rather than calling it optionally: `saveRemoteFiles?.()` would resolve to `undefined`, which is
        // indistinguishable from success, so the click would report nothing and produce nothing.
        if (!saveRemoteFiles) throw new Error("useQuestionFields: saveRemoteFiles was not provided");
        await saveRemoteFiles(
          downloadable.map((file, index) => ({
            name: uniqueFileName(file.name || `${index + 1}`, taken),
            url: attachmentFileUrl(file.awsKey),
          })),
          bulkDownloadName(),
        );
      } catch (error) {
        console.error(error);
        dispatch(
          showToastNotification({
            content: t("Could not download the files") as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      } finally {
        setIsDownloadingAllFiles(false);
      }
    }

    if (skippedCount) {
      dispatch(
        showToastNotification({
          content: t("{{count}} file(s) from the File Manager were not included", {
            count: skippedCount,
          }) as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  /**
   * Opens the "delete all files" confirmation, holding the removal until it is accepted. Only the
   * confirmation's state lives here — each platform renders it in its own idiom (a modal on web, a sheet
   * on native) off `isDeleteAllFilesModalOpen`.
   */
  const handleDeleteAllFilesClick = () => setIsDeleteAllFilesModalOpen(true);

  const closeDeleteAllFilesModal = () => setIsDeleteAllFilesModalOpen(false);

  const confirmDeleteAllFiles = () => {
    setIsDeleteAllFilesModalOpen(false);
    if (!isStoredQuestion(eachField)) return;
    handleRemoveAllUploadedFiles?.({
      eachField,
      sectionIndex: sectionIndex!,
      fieldIndex: fieldIndex!,
      sectionId,
      questionId,
      source,
      tableRowIndex,
      // The handler takes the file-typed answer; `answerFiles` is that same list already read out
      // of the `z.unknown()` value above, so it is passed rather than the raw value re-asserted.
      currentAnswer: { ...currentAnswer, value: answerFiles },
      handleCustomFieldData,
    });
  };

  return {
    questionId,
    name,
    sectionId,
    currentAnswer,
    isCurrentUserAllowedToFill,
    isCurrentUserAllowedToModify,
    enabled,
    permissions,
    setOpenUrlPopUpState,
    openUrlPopUpState,
    formulaValue,
    concatenatedValue,
    autoNumberValue,
    answerFiles,
    handleDownloadAllFiles,
    isDownloadingAllFiles,
    handleDeleteAllFilesClick,
    isDeleteAllFilesModalOpen,
    closeDeleteAllFilesModal,
    confirmDeleteAllFiles,
  };
}

export default useQuestionFields;
